/**
 * Chart tokens for the Usage Analytics board.
 *
 * These hexes are a VALIDATED pair, not a taste call: run through the palette checks against this
 * app's white chart surface, they clear the lightness band, the chroma floor, colour-vision
 * separation (worst pair ΔE 21.6 protan, target ≥8), the normal-vision floor (32.3, floor ≥15) and
 * 3:1 contrast. If either value changes, re-validate — "it looks fine" is not the bar, because the
 * readers this protects are exactly the ones who cannot check for themselves.
 *
 * People Space is a LIGHT-ONLY app (`theme: 'light'` in config.ts), so there is deliberately no
 * dark set here. Adding one means re-stepping both hues for the dark surface and re-validating —
 * a flipped light palette is not a dark palette.
 *
 * Ink is separate from series colour on purpose: marks carry the data colour, text never does. A
 * label in a mid-blue on white is harder to read than the same label in ink, and colouring text
 * would make hue the only identity channel.
 */

/** Series colour for volume — the single hue every magnitude chart here uses. */
export const SERIES_PRIMARY = '#2a78d6';
/** Series colour for failures. A categorical slot, NOT the reserved status red. */
export const SERIES_ERROR = '#e34948';

/** The surface charts are drawn on. The validator's contrast checks assume this exact value. */
export const CHART_SURFACE = '#ffffff';

export const INK_SECONDARY = '#52514e';
export const INK_MUTED = '#898781';
export const GRIDLINE = '#e1e0d9';
export const AXIS = '#c3c2b7';

/** Area fills are a wash, never a saturated block — the line carries the shape. */
export const AREA_OPACITY = 0.1;

/** Bars never fill their slot; the leftover band is the air that separates them. */
export const MAX_BAR_THICKNESS = 24;
/** Rounded data-end. Square at the baseline, so bars grow from one flat line. */
export const BAR_RADIUS = 4;

/**
 * Compact form for headline figures: 1,284 · 12.9K · 1.2M. Big standalone numbers keep the font's
 * proportional figures (tabular-nums is for columns that must align vertically, and makes a value
 * like 121 look loose at display sizes).
 */
export function formatCompact(value: number): string {
  if (!Number.isFinite(value)) return '—';
  if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (Math.abs(value) >= 10_000) return `${(value / 1000).toFixed(1)}K`;
  return value.toLocaleString('en-US');
}

/** Percentages read better with one decimal below 10% and none above. */
export function formatPercent(rate: number): string {
  if (!Number.isFinite(rate)) return '—';
  const pct = rate * 100;
  return `${pct >= 10 ? Math.round(pct) : pct.toFixed(1)}%`;
}

/**
 * Durations read as durations: 40m, 3.2h, 2.1d — never "0.03 days".
 *
 * Lives here beside the other formatters because three panels now show hours (response times,
 * interview outcomes, the baseline comparison) and three copies of the same thresholds would drift.
 */
export function formatHours(hours: number | null): string {
  if (hours === null || !Number.isFinite(hours)) return '—';
  if (hours < 1) return `${Math.round(hours * 60)}m`;
  if (hours < 48) return `${hours < 10 ? hours.toFixed(1) : Math.round(hours)}h`;
  return `${(hours / 24).toFixed(1)}d`;
}

/** `2026-08-12` → `12 Aug`. Axis ticks and tooltips, never a full ISO string. */
export function formatDay(iso: string): string {
  const date = new Date(`${iso}T12:00:00Z`);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

/**
 * Axis ticks rounded to clean numbers — they carry every value that is not directly labelled, so
 * they have to be readable at a glance rather than exact.
 */
export function niceTicks(max: number, count = 3): number[] {
  if (max <= 0) return [0];
  const rawStep = max / count;
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((s) => s >= rawStep) ?? magnitude * 10;
  // Round the TOP up to a whole step. Stopping at the last step below `max` would leave the
  // highest value sitting above the top gridline, which reads as data escaping the chart.
  const top = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  // Rounding to whole numbers collapses a small series: with a max of 1 the steps are 0/0.5/1 and
  // Math.round turns them into 0/1/1 — a duplicated gridline and a wrong label. Round only when
  // the step is at least 1, and keep one decimal below that.
  const clean = (value: number) => (step >= 1 ? Math.round(value) : Math.round(value * 10) / 10);
  for (let value = 0; value <= top + step / 1000; value += step) ticks.push(clean(value));
  return ticks;
}

/** `accepted_other_offer` → `Accepted other offer`. Reason codes are for machines; the tables are read. */
export function humaniseCode(code: string): string {
  if (code === 'not_given') return 'No reason given';
  const words = code.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}
